import { Link } from "react-router-dom";

const navItems = ["Features", "How it works", "Why Datascope", "Security"];
const demoUrl = "https://calendly.com/satya-periscopechat/periscope-demo";

function CheckItem({
  title,
  description,
  tone = "pink",
}: {
  title: string;
  description: string;
  tone?: "yellow" | "pink" | "purple";
}) {
  const toneClass =
    tone === "yellow"
      ? "bg-[#fff1be]/30"
      : tone === "purple"
        ? "bg-[#b060ff]/20"
        : "bg-[#ee87cb]/20";

  return (
    <li className="flex gap-3">
      <div
        className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${toneClass}`}
      >
        <span className="text-sm font-semibold text-gray-950">✓</span>
      </div>
      <div>
        <p className="font-medium text-gray-950">{title}</p>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
    </li>
  );
}

export function LandingPage() {
  return (
    <div className="min-h-screen overflow-hidden bg-white text-gray-950">
      <div className="relative">
        <div
          className="landing-hero-gradient absolute inset-2 bottom-0 rounded-[2rem] opacity-20 ring-1 ring-black/5 ring-inset"
        />
        <div className="relative px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:max-w-7xl">
            <header className="flex items-center justify-between pt-12 sm:pt-16">
              <Link to="/" className="flex items-center gap-2">
                <img
                  src="/querygen-logo.png"
                  alt="Periscope Logo"
                  className="h-10 w-auto"
                />
                <span className="text-2xl font-bold tracking-tight">
                  Datascope
                </span>
              </Link>

              <nav className="hidden items-center md:flex">
                {navItems.map((item) => (
                  <a
                    key={item}
                    href={`#${item.toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex items-center px-4 py-3 text-base font-medium text-gray-950 hover:bg-black/[2.5%]"
                  >
                    {item}
                  </a>
                ))}
              </nav>

              <div className="flex items-center gap-3">
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-full border border-gray-950/10 px-4 py-[7px] text-base font-medium text-gray-950 shadow-sm hover:bg-gray-50"
                >
                  Login
                </Link>
                <a
                  href={demoUrl}
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-full border border-transparent bg-gray-950 px-4 py-[7px] text-base font-medium text-white shadow-md hover:bg-gray-800"
                >
                  Get Access
                </a>
              </div>
            </header>

            <div className="pb-24 pt-16 sm:pb-32 sm:pt-24 md:pb-48 md:pt-32">
              <h1 className="max-w-5xl text-6xl font-medium leading-[0.9] tracking-tight text-gray-950 [text-wrap:balance] sm:text-8xl sm:leading-[0.8] md:text-9xl md:leading-[0.8]">
                Beyond SQL Generation
              </h1>
              <p className="mt-8 max-w-lg text-xl font-medium leading-7 text-gray-950/75 sm:text-2xl sm:leading-8">
                Datascope understands your data, generates precise queries, and
                delivers insights that matter.
              </p>
              <div className="mt-12 flex flex-col gap-x-6 gap-y-4 sm:flex-row">
                <a
                  href="#contact"
                  className="relative inline-flex items-center justify-center whitespace-nowrap rounded-full border border-transparent bg-white/15 px-4 py-[7px] text-base font-medium text-gray-950 shadow-md ring-1 ring-[#D15052]/15 after:absolute after:inset-0 after:rounded-full after:shadow-[inset_0_0_2px_1px_#ffffff4d] hover:bg-white/20"
                >
                  Request access
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main>
        <div className="bg-gradient-to-b from-white from-50% to-gray-100 py-32">
          <div className="px-6 pb-24 lg:px-8">
            <div className="mx-auto max-w-2xl lg:max-w-7xl">
              <div className="overflow-hidden pb-24">
                <h2 className="max-w-3xl text-4xl font-medium tracking-tight text-gray-950 [text-wrap:balance] sm:text-6xl">
                  A smarter way to explore your data.
                </h2>
                <div className="mt-16 overflow-hidden rounded-lg border border-black/5 shadow-md sm:h-auto sm:w-full">
                  <div className="flex h-full w-full items-center justify-center bg-gray-100">
                    <img
                      src="/marketing/hero_final.png"
                      alt="Datascope Interface"
                      className="h-full w-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <section id="features" className="px-6 lg:px-8">
            <div className="mx-auto max-w-2xl lg:max-w-7xl">
              <div className="mb-10">
                <h4 className="font-mono text-xs font-semibold uppercase tracking-widest text-gray-500">
                  Features
                </h4>
                <h2 className="mt-2 max-w-3xl text-4xl font-medium tracking-tight text-gray-950 [text-wrap:balance] sm:text-6xl">
                  How Datascope works
                </h2>
              </div>

              <div className="mt-10 grid grid-cols-1 gap-4 sm:mt-16 lg:grid-cols-6">
                <div className="relative overflow-hidden rounded-3xl bg-white p-8 shadow-sm ring-1 ring-black/5 lg:col-span-3 lg:rounded-tl-[2rem]">
                  <div className="mb-2 text-sm font-medium text-[#D15052]">
                    Connect
                  </div>
                  <h3 className="mb-4 text-xl font-semibold">
                    Seamless Database Integration
                  </h3>
                  <p className="mb-4 text-gray-950/75">
                    Securely connect to your Postgres database with MongoDB
                    support coming soon. No data leaves your environment.
                  </p>
                  <div className="mt-4 flex h-auto w-full items-center justify-center rounded-lg bg-[#fff1be]/10">
                    <img
                      src="/marketing/connect_securely.png"
                      alt="Secure database connection"
                      className="h-full w-full rounded-lg border border-black/10 object-contain"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <img
                      src="/marketing/postgres.png"
                      className="h-12 w-auto"
                      alt="Postgres Logo"
                    />
                    <img
                      src="/marketing/mongodb.png"
                      className="h-12 w-auto"
                      alt="MongoDB Logo"
                    />
                    <img
                      src="/marketing/google-sheets-full-logo-1.svg"
                      className="h-24 w-auto"
                      alt="Google Sheets Logo"
                    />
                  </div>
                </div>

                <div className="relative overflow-hidden rounded-3xl bg-white p-8 shadow-sm ring-1 ring-black/5 lg:col-span-3 lg:rounded-tr-[2rem]">
                  <div className="mb-2 text-sm font-medium text-[#D15052]">
                    Interact
                  </div>
                  <h3 className="mb-4 text-xl font-semibold">
                    Natural Language Interface
                  </h3>
                  <p className="mb-4 text-gray-950/75">
                    From simple metrics to complex data explorations, ask in
                    your language, not the database&apos;s.
                  </p>
                  <div className="mt-4 h-auto w-full rounded-lg bg-[#ee87cb]/10">
                    <img
                      src="/marketing/natural_language_ui_final.png"
                      alt="Natural Language Interface"
                      className="h-full w-full rounded-lg border border-black/5 object-contain"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-10 grid grid-cols-1 gap-4 sm:mt-16 lg:grid-cols-6">
                <div className="relative overflow-hidden rounded-3xl bg-white p-8 shadow-sm ring-1 ring-black/5 lg:col-span-3 lg:rounded-bl-[2rem]">
                  <div className="mb-2 text-sm font-medium text-[#D15052]">
                    Analyze
                  </div>
                  <h3 className="mb-4 text-xl font-semibold">
                    Deeper Insights
                  </h3>
                  <p className="text-gray-950/75">
                    Get visualizations, trend analyses, and anomaly detection
                    with full query transparency.
                  </p>
                  <div className="mt-4 flex h-auto w-full items-center justify-center rounded-lg bg-[#b060ff]/10">
                    <img
                      src="/marketing/deeper_insights.png"
                      alt="Datascope insights"
                      className="h-auto w-full rounded-lg border border-black/5 object-cover"
                    />
                  </div>
                </div>

                <div className="relative overflow-hidden rounded-3xl bg-white p-8 shadow-sm ring-1 ring-black/5 lg:col-span-3">
                  <div className="mb-2 text-sm font-medium text-[#D15052]">
                    Customize
                  </div>
                  <h3 className="mb-4 text-xl font-semibold">Flexible AI</h3>
                  <p className="text-gray-950/75">
                    Configure behavior to match your team&apos;s specific needs
                    and domain expertise.
                  </p>
                  <div className="mt-4 flex h-auto w-full items-center justify-center rounded-lg bg-[#ee87cb]/10">
                    <img
                      src="/marketing/flexible_ai.gif"
                      alt="Customize AI"
                      className="h-full w-full rounded-lg border border-black/5 object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="mx-2 mt-2 rounded-[2rem] bg-gray-900 py-32">
          <section id="why-datascope" className="px-6 lg:px-8">
            <div className="mx-auto max-w-2xl lg:max-w-7xl">
              <h4 className="font-mono text-xs font-semibold uppercase tracking-widest text-gray-400">
                Comparison
              </h4>
              <h2 className="mt-2 max-w-3xl text-4xl font-medium tracking-tight text-white [text-wrap:balance] sm:text-6xl">
                Why Datascope Stands Apart
              </h2>

              <div className="mt-10 grid grid-cols-1 gap-4 sm:mt-16 lg:grid-cols-6 lg:grid-rows-2">
                <div className="relative overflow-hidden rounded-3xl bg-gray-800 p-8 shadow-sm ring-1 ring-white/5 lg:col-span-4 lg:rounded-tl-[2rem]">
                  <div className="mb-2 text-sm font-medium text-[#ee87cb]">
                    Multi-agent Architecture
                  </div>
                  <h3 className="mb-4 text-xl font-semibold text-white">
                    Beyond Single-Model Solutions
                  </h3>
                  <p className="mb-4 text-white/75">
                    While others rely on a single LLM, Datascope orchestrates
                    specialized agents working in concert for deeper
                    understanding and better results.
                  </p>
                </div>
                <div className="relative overflow-hidden rounded-3xl bg-gray-800 p-8 shadow-sm ring-1 ring-white/5 lg:col-span-2 lg:rounded-tr-[2rem]">
                  <div className="mb-2 text-sm font-medium text-[#ee87cb]">
                    Deep Reasoning
                  </div>
                  <h3 className="mb-4 text-xl font-semibold text-white">
                    Multi-step Analysis
                  </h3>
                  <p className="mb-4 text-white/75">
                    Solve complex analytical challenges requiring sophisticated
                    reasoning and multi-query chains.
                  </p>
                </div>
                <div className="relative overflow-hidden rounded-3xl bg-gray-800 p-8 shadow-sm ring-1 ring-white/5 lg:col-span-2 lg:rounded-bl-[2rem]">
                  <div className="mb-2 text-sm font-medium text-[#ee87cb]">
                    Transparency
                  </div>
                  <h3 className="mb-4 text-xl font-semibold text-white">
                    Full Visibility
                  </h3>
                  <p className="text-white/75">
                    View and verify the exact SQL queries behind every result.
                  </p>
                </div>
                <div className="relative overflow-hidden rounded-3xl bg-gray-800 p-8 shadow-sm ring-1 ring-white/5 lg:col-span-4 lg:rounded-br-[2rem]">
                  <div className="mb-2 text-sm font-medium text-[#ee87cb]">
                    Enterprise Security
                  </div>
                  <h3 className="mb-4 text-xl font-semibold text-white">
                    Your Data, Your Control
                  </h3>
                  <p className="text-white/75">
                    On-premise deployment, no data storage, and customizable AI
                    profiles ensure complete data sovereignty.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>

        <section id="how-it-works" className="px-6 py-32 lg:px-8">
          <div className="mx-auto max-w-2xl lg:max-w-7xl">
            <h4 className="font-mono text-xs font-semibold uppercase tracking-widest text-gray-500">
              Architecture
            </h4>
            <h2 className="mt-2 max-w-3xl text-4xl font-medium tracking-tight text-gray-950 [text-wrap:balance] sm:text-6xl">
              Not Just AI, an Orchestration of Specialists
            </h2>

            <div className="mt-10 grid grid-cols-1 items-center gap-8 sm:mt-16 lg:grid-cols-2">
              <div>
                <h3 className="mb-6 text-2xl font-semibold">
                  Multi-Agent Architecture
                </h3>
                <ul className="space-y-4">
                  <CheckItem
                    title="Schema understanding agent"
                    description="Maps and analyzes your database structure for optimal query planning"
                  />
                  <CheckItem
                    title="Query generation agent"
                    description="Creates precise, optimized SQL based on your natural language request"
                  />
                  <CheckItem
                    title="Analysis agent"
                    description="Identifies patterns, anomalies, and key insights in query results"
                    tone="purple"
                  />
                  <CheckItem
                    title="Reasoning coordinator"
                    description="Orchestrates complex multi-step analysis for deep insights"
                  />
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="security" className="bg-gray-50 px-6 py-32 lg:px-8">
          <div className="mx-auto max-w-2xl lg:max-w-7xl">
            <h4 className="font-mono text-xs font-semibold uppercase tracking-widest text-gray-500">
              Security
            </h4>
            <h2 className="mt-2 max-w-3xl text-4xl font-medium tracking-tight text-gray-950 [text-wrap:balance] sm:text-6xl">
              Enterprise-Grade Security
            </h2>

            <div className="mt-10 grid grid-cols-1 items-center gap-16 md:grid-cols-2">
              <div className="order-1 md:order-2">
                <h3 className="mb-6 text-2xl font-semibold">
                  Your Data, Your Control
                </h3>
                <ul className="space-y-4">
                  <CheckItem
                    title="On-premise deployment"
                    description="Keep your data within your network boundaries"
                    tone="yellow"
                  />
                  <CheckItem
                    title="No data storage"
                    description="We only maintain chat history, not your database contents"
                  />
                  <CheckItem
                    title="Advanced AI models"
                    description="Powered by GPT-5.6 Luna for data and knowledge workflows"
                    tone="purple"
                  />
                  <CheckItem
                    title="Customizable AI profile"
                    description="Tune the AI to match your specific business needs"
                  />
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="px-6 py-32 text-center lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="mb-6 text-4xl font-medium tracking-tight text-gray-950 [text-wrap:balance] sm:text-6xl">
              Ready to transform your data experience?
            </h2>
            <p className="mb-8 text-xl font-medium leading-7 text-gray-950/75">
              Join the growing number of organizations that are unlocking
              insights from their data with Datascope.
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <a
                href={demoUrl}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-full border border-transparent bg-gray-950 px-4 py-[7px] text-base font-medium text-white shadow-md hover:bg-gray-800"
              >
                Schedule a demo
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-black/5 px-6 py-12 lg:px-8">
        <div className="mx-auto flex max-w-2xl flex-col items-center justify-between lg:max-w-7xl md:flex-row">
          <div className="mb-6 md:mb-0">
            <div className="mb-2 flex items-center gap-2">
              <img
                src="/querygen-logo.png"
                alt="Periscope Logo"
                className="h-8 w-auto"
              />
              <h2 className="text-xl font-bold">Datascope</h2>
            </div>
            <p className="text-sm text-gray-500">
              © 2023 Periscope. All rights reserved.
            </p>
          </div>
          <div className="flex gap-8">
            <a
              href="https://periscopechat.com/Privacy"
              className="text-sm text-gray-500 hover:text-gray-950"
            >
              Privacy
            </a>
            <a
              href="https://periscopechat.com/Terms"
              className="text-sm text-gray-500 hover:text-gray-950"
            >
              Terms
            </a>
            <a
              href="https://periscopechat.com/contact-us"
              className="text-sm text-gray-500 hover:text-gray-950"
            >
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
